import React from 'react';
import {
  IconAlertCircle,
  IconCircleCheck,
  IconCircleDashed,
  IconFileAlert,
} from '@tabler/icons-react';
import { Alert } from 'src/design/atoms/Alert';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import {
  DesignModelFileKind,
  missingFilesSentence,
} from 'src/routes/Jobs/DesignModels/designModelFiles';
import { failedStatusText } from 'src/routes/Jobs/DesignModels/designModelStatus';

export interface IDesignModelStatusStripProps {
  conversionStatus: string | null | undefined;
  conversionError: string | null | undefined;
  missing: DesignModelFileKind[];
  /** Every slot has a saved file, so a null status can be fixed by Reprocess alone. */
  allFilesSaved: boolean;
  refreshFailed: boolean;
  refreshing: boolean;
  onRetry: () => void;
  reprocessError: string | null;
}

const StatusRow: React.FC<{ icon: React.ReactNode; text: string }> = ({
  icon,
  text,
}) => (
  <Group gap="xs" wrap="nowrap">
    {icon}
    <Text size="sm" c="bright">
      {text}
    </Text>
  </Group>
);

export const DesignModelStatusStrip: React.FC<IDesignModelStatusStripProps> = ({
  conversionStatus,
  conversionError,
  missing,
  allFilesSaved,
  refreshFailed,
  refreshing,
  onRetry,
  reprocessError,
}) => {
  const failed = conversionStatus === 'Failed';

  return (
    <Stack gap="xs">
      {failed ? (
        <Alert
          color="red"
          title="Processing failed"
          icon={<IconAlertCircle size={16} />}
        >
          {failedStatusText(conversionError, missing)}
        </Alert>
      ) : conversionStatus === 'Processing' ? (
        <StatusRow
          icon={<Loader size={14} color="blue" />}
          text="Processing. You can leave this page; processing continues."
        />
      ) : conversionStatus === 'Ready' ? (
        <StatusRow
          icon={
            <IconCircleCheck size={18} color="var(--mantine-color-green-7)" />
          }
          text="Ready for the Progress viewer"
        />
      ) : (
        <StatusRow
          icon={
            <IconCircleDashed size={18} color="var(--mantine-color-gray-6)" />
          }
          text={
            allFilesSaved
              ? 'Not processed. Use Reprocess to start.'
              : 'Not processed. Add all three files and save.'
          }
        />
      )}
      {!failed && missing.length > 0 && (
        <StatusRow
          icon={<IconFileAlert size={18} color="var(--mantine-color-red-7)" />}
          text={missingFilesSentence(missing)}
        />
      )}
      {refreshFailed && (
        <Group gap="xs">
          <Text size="sm" c="dimmed">
            Couldn&apos;t refresh status
          </Text>
          <BasicButton
            variant="subtle"
            size="compact-sm"
            loading={refreshing}
            onClick={onRetry}
          >
            Retry
          </BasicButton>
        </Group>
      )}
      {reprocessError && (
        <Alert color="red" title="Reprocess did not start">
          {reprocessError}
        </Alert>
      )}
    </Stack>
  );
};
